import React, { useState, useRef, useEffect } from 'react';
import Icon from '../icon/Icon';
import P from '../texts/p/P';
import * as S from './ImageUploader.styles';
import { ImageUploaderProps } from './ImageUploader.types';
import {
  validateFileType,
  validateFileSize,
  validateImageDimensions,
  formatConstraintsText,
} from './ImageUploader.utils';

const defaultProps: Partial<ImageUploaderProps> = {
  acceptedFormats: ['.png', '.jpg', '.jpeg', '.svg'],
  maxSizeKB: 200,
  dimensions: undefined,
  initialImage: undefined,
  onImageLoad: undefined,
  onError: undefined,
};

const ImageUploader: React.FC<ImageUploaderProps> = (props) => {
  const {
    acceptedFormats = defaultProps.acceptedFormats!,
    maxSizeKB = defaultProps.maxSizeKB!,
    dimensions,
    initialImage,
    onImageLoad,
    onError,
  } = props;

  const [imageUrl, setImageUrl] = useState<string | null>(initialImage || null);
  const [error, setError] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // Actualizar imagen cuando cambie initialImage
  useEffect(() => {
    if (initialImage) {
      setImageUrl(initialImage);
    }
  }, [initialImage]);

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    setError(null);

    // Validar tipo de archivo
    const typeValidation = validateFileType(file, acceptedFormats);
    if (!typeValidation.isValid) {
      setError(typeValidation.error!);
      onError?.(typeValidation.error!);
      return;
    }

    // Validar tamaño
    const sizeValidation = validateFileSize(file, maxSizeKB);
    if (!sizeValidation.isValid) {
      setError(sizeValidation.error!);
      onError?.(sizeValidation.error!);
      return;
    }

    // Leer archivo y validar dimensiones si aplica
    const reader = new FileReader();
    reader.onload = (e) => {
      const result = e.target?.result as string;

      if (dimensions) {
        // Validar dimensiones
        const img = new Image();
        img.onload = () => {
          const dimensionsValidation = validateImageDimensions(img, dimensions);
          if (!dimensionsValidation.isValid) {
            setError(dimensionsValidation.error!);
            onError?.(dimensionsValidation.error!);
            return;
          }

          // Todo OK
          setImageUrl(result);
          onImageLoad?.(file, result);
        };
        img.src = result;
      } else {
        // Sin validación de dimensiones
        setImageUrl(result);
        onImageLoad?.(file, result);
      }
    };

    reader.readAsDataURL(file);
  };

  const handleClick = () => {
    if (!imageUrl) {
      inputRef.current?.click();
    }
  };

  const handleDelete = (e: React.MouseEvent) => {
    e.stopPropagation();
    setImageUrl(null);
    setError(null);
    if (inputRef.current) {
      inputRef.current.value = '';
    }
  };

  const constraintsText = formatConstraintsText(
    acceptedFormats,
    maxSizeKB,
    dimensions
  );

  return (
    <S.Container>
      <S.UploadAreaWrapper>
        {imageUrl && (
          <S.DeleteButton onClick={handleDelete} type="button">
            <Icon name='delete_outline' variant='danger' width={20} height={20} />
          </S.DeleteButton>
        )}
        <S.UploadArea hasImage={!!imageUrl} onClick={handleClick}>
        <input
          ref={inputRef}
          type='file'
          accept={acceptedFormats.join(',')}
          onChange={handleFileChange}
        />
        {imageUrl ? (
          <S.ImagePreview>
            <img src={imageUrl} alt='Imagen cargada' />
          </S.ImagePreview>
        ) : (
          <S.UploadContent>
            <Icon name='plus_icon' variant='primary' width={24} height={24} />
            <P type='3' variant='primary' modifier='regular' withMargin={false}>
              Cargar archivo
            </P>
          </S.UploadContent>
        )}
      </S.UploadArea>
      </S.UploadAreaWrapper>

      {!error && (
        <S.InfoText>
          <P type='4' variant='neutral' modifier='weak' withMargin={false}>
            {constraintsText}
          </P>
        </S.InfoText>
      )}

      {error && (
        <S.ErrorText>
          <P type='4' variant='danger' modifier='regular' withMargin={false}>
            {error}
          </P>
        </S.ErrorText>
      )}
    </S.Container>
  );
};

export default ImageUploader;
